/**
 * Add to Mimir (ACP-D48, docs/gfx-mimir-publish/plan.md): a published template version made into a new Mimir graphics
 * item on the tenant's Mimir. The cloud does it server-side — the Mimir API key never reaches a browser — with
 * `@ssoreide/gfx-mimir`'s upload. Each tenant configures its own Mimir (ACP-D49: a base URL, a form id and a write-only
 * API key, `GET/PUT/DELETE /graphics/settings/mimir`). Routes: `graphicsMimirRoutes.ts`.
 */
import { z } from 'zod';
import { isoTimestampWireSchema, sha256HexWireSchema } from './primitives.js';

export const addGraphicsTemplateVersionToMimirRequestWireSchema = z
  .object({
    /**
     * A 320×240 PNG poster for the item, uploaded first as a content object (`POST /graphics/content/uploads`), or
     * null for none. `validation-failed` naming `thumbnailSha256` when the tenant has no such object.
     */
    thumbnailSha256: sha256HexWireSchema.nullable(),
  })
  .strict();
export type AddGraphicsTemplateVersionToMimirRequestWire = z.infer<typeof addGraphicsTemplateVersionToMimirRequestWireSchema>;

export const addGraphicsTemplateVersionToMimirResponseWireSchema = z
  .object({
    /** The new Mimir graphics item's id (also appended to the version's `mimirItems`). */
    itemId: z.string(),
    /** Its title in Mimir: the version's name and number (`Lower third (v3)`). */
    title: z.string(),
  })
  .strict();
export type AddGraphicsTemplateVersionToMimirResponseWire = z.infer<typeof addGraphicsTemplateVersionToMimirResponseWireSchema>;

export const graphicsMimirStatusResponseWireSchema = z
  .object({
    /**
     * Whether Add to Mimir can work for the caller's tenant: the tenant's Mimir settings complete (base URL, form id, API
     * key), the base URL's host still on the stage's allowlist, and a hosted module whose `gfx-mimir.json` can be read;
     * the designer hides Add to Mimir without them.
     */
    configured: z.boolean(),
    /** Whether the caller holds `graphics.mimir` tenant-wide. */
    permitted: z.boolean(),
  })
  .strict();
export type GraphicsMimirStatusResponseWire = z.infer<typeof graphicsMimirStatusResponseWireSchema>;

// ─── the tenant's Mimir settings (ACP-D49) ───

/** The longest base URL, form id and API key a tenant may save. */
export const MAX_MIMIR_BASE_URL_LENGTH = 255;
export const MAX_MIMIR_FORM_ID_LENGTH = 256;
export const MAX_MIMIR_API_KEY_LENGTH = 4096;

export const graphicsMimirSettingsWireSchema = z
  .object({
    /** The tenant's Mimir origin (`https://research4.mimir.tv`), or null when none is set. */
    baseUrl: z.string().nullable(),
    /** The Mimir metadata form new items are made with, or null when none is set. */
    formId: z.string().nullable(),
    /** Whether an API key is stored. The key itself is write-only: no answer ever carries it. */
    apiKeySet: z.boolean(),
    /** When the settings were last saved; null when none are set. */
    updatedAt: isoTimestampWireSchema.nullable(),
    /** The stage's allowed host suffixes: a base URL's host must be a subdomain of one (empty: Add to Mimir is off on this stage). */
    allowedHostSuffixes: z.array(z.string()),
  })
  .strict();
export type GraphicsMimirSettingsWire = z.infer<typeof graphicsMimirSettingsWireSchema>;

export const updateGraphicsMimirSettingsRequestWireSchema = z
  .object({
    /** `https://<host>` — no port, path, query or credentials — whose host is a subdomain of an allowed suffix. */
    baseUrl: z.string().min(1).max(MAX_MIMIR_BASE_URL_LENGTH),
    formId: z
      .string()
      .min(1)
      .max(MAX_MIMIR_FORM_ID_LENGTH)
      .regex(/^\S+$/, 'a form id has no whitespace'),
    /** A new API key, or null to keep the stored one (`validation-failed` naming `apiKey` when none is stored). */
    apiKey: z
      .string()
      .min(1)
      .max(MAX_MIMIR_API_KEY_LENGTH)
      .regex(/^\S+$/, 'an API key has no whitespace')
      .nullable(),
  })
  .strict();
export type UpdateGraphicsMimirSettingsRequestWire = z.infer<typeof updateGraphicsMimirSettingsRequestWireSchema>;
